import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from 'lucide-react';
import { BrandMark } from './BrandMark';
import { useLocale } from '../contexts/LocaleContext';

/**
 * The single layout for the entry flow (language, auth, onboarding).
 * Reuses the approved sand canvas, brand mark and spacing — no new chrome.
 */
export function EntryLayout({
  title,
  body,
  step,
  totalSteps,
  showBack = true,
  footer,
  children
}: {
  title: string;
  body?: string;
  step?: number;
  totalSteps?: number;
  showBack?: boolean;
  /** Sticky action area, kept above the safe area. */
  footer?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  return <div className="flex min-h-screen w-full flex-col bg-sand" data-id="element-748" data-mp="py3ujnlpc46g">
      <header className="flex items-center gap-2 px-2 pb-1 pt-[max(12px,env(safe-area-inset-top))]" data-id="element-749" data-mp="26d3xhlri04ns">
        {showBack ? <button type="button" onClick={() => navigate(-1)} aria-label={t('onb.back')} className="grid h-11 w-11 shrink-0 place-items-center rounded-pill text-deep transition-colors duration-150 ease-brand hover:bg-sand-deep" data-id="element-750" data-mp="21eakdug4ifo5">
            <ChevronLeftIcon size={20} data-id="element-751" data-mp="209cf9wv5iwcn" />
          </button> : <span className="grid h-11 w-11 shrink-0 place-items-center" data-id="element-752" data-mp="257nnk35ssysr">
            <BrandMark size={24} data-id="element-753" data-mp="254rno9ae401o" />
          </span>}

        {step && totalSteps ? <div className="min-w-0 flex-1" data-id="element-754" data-mp="17hj088qtwgx1">
            <p className="bee-num text-[11px] font-medium text-muted" data-id="element-755" data-mp="1chos9q10g5fn">
              {t('onb.step', {
            current: step,
            total: totalSteps
          })}
            </p>
            <div className="mt-1 flex gap-1" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={totalSteps} data-id="element-756" data-mp="1hfqbcayfs4r4">
              {Array.from({
            length: totalSteps
          }).map((_, index) => <span key={index} className={`h-[3px] flex-1 rounded-pill ${index < step ? 'bg-gold' : 'bg-sand-deep'}`} data-id="element-757" data-mp="tpw203dbsybm" data-mpi={index} />)}
            </div>
          </div> : <span className="flex-1" data-id="element-758" data-mp="vgkoqmnag8iw" />}
      </header>

      <main className="flex-1 px-2 pb-3" data-id="element-759" data-mp="qhhjp27b62ax">
        <h1 className="mt-2 text-title text-deep" data-id="element-760" data-mp="qij6g3xosd8h">{title}</h1>
        {body && <p className="mt-1 max-w-[42ch] text-[13px] leading-relaxed text-muted" data-id="element-761" data-mp="2t6oaekdsuu1">
            {body}
          </p>}
        <div className="mt-3" data-id="element-762" data-mp="3uy0reay4a3za">{children}</div>
      </main>

      {footer && <div className="sticky bottom-0 border-t border-sand-line bg-sand/95 px-2 pb-[max(12px,env(safe-area-inset-bottom))] pt-2 backdrop-blur" data-id="element-763" data-mp="cre5myil1ewk">
          {footer}
        </div>}
    </div>;
}

/** Selectable row used by language, units, goals and activities. */
export function ChoiceRow({
  label,
  hint,
  selected,
  onClick,
  icon
}: {
  label: string;
  hint?: string;
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-pressed={selected} className={`flex w-full items-center gap-2 rounded-card border px-2 py-2 text-left transition-[background-color,border-color] duration-150 ease-brand ${selected ? 'border-gold bg-white' : 'border-sand-line bg-white/60 hover:border-deep/25'}`} data-id="element-764" data-mp="363we4rfku8d1">
      {icon && <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-[10px] ${selected ? 'bg-gold/15 text-gold-deep' : 'bg-sand-deep text-soft'}`} data-id="element-765" data-mp="3oue08d5wm5pf">
          {icon}
        </span>}
      <span className="min-w-0 flex-1" data-id="element-766" data-mp="3fzzogkkl1db2">
        <span className="block text-[13.5px] font-medium text-deep" data-id="element-767" data-mp="7lr2hvckfkrv">{label}</span>
        {hint && <span className="mt-[2px] block text-[11.5px] leading-snug text-muted" data-id="element-768" data-mp="cch7v8kzrnmk">
            {hint}
          </span>}
      </span>
      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-pill border ${selected ? 'border-gold bg-gold' : 'border-deep/25'}`} aria-hidden="true" data-id="element-769" data-mp="2jx3kwpmfdopc">
        {selected && <span className="h-[7px] w-[7px] rounded-pill bg-deep" data-id="element-770" data-mp="2avm7xg4be34v" />}
      </span>
    </button>;
}